/**
 * Export and import, as two buttons and everything that has to happen between them.
 *
 * Browser storage is one "clear site data" away from empty, so the file this panel writes is the
 * only copy of a character that outlives the tab, and the only way one reaches another machine.
 *
 * The import is deliberately slow. It picks, checks the size, parses, validates through the same
 * gate a record off disk goes through, says what it is about to do, waits to be told to do it,
 * downloads the character that is about to be written over, and only then writes. Every one of
 * those steps can stop, and none of them touches storage until the last one, so a file that is not
 * a save cannot cost anyone the character they already had.
 *
 * A refusal is read out specifically. "Import failed" is what this exists to avoid: the wording
 * comes from `saveFailureText`, which has a sentence for every way a save can be refused.
 *
 * It is a component rather than part of the menu because the settings screen (#33) mounts the same
 * thing, and a second copy of this sequence is a second place for it to be subtly different.
 */

import {
  SAVE_ADD,
  SAVE_BACKUP_REFUSED,
  SAVE_CANCEL,
  SAVE_CANCELLED,
  SAVE_CHECKED,
  SAVE_DOWNLOAD_REFUSED,
  SAVE_EXPORT,
  SAVE_IMPORT,
  SAVE_NO_FILE,
  SAVE_REPLACE,
  SAVE_UNREADABLE_FILE,
  saveAddWarning,
  saveExported,
  saveImported,
  saveReplaceWarning,
} from '@/text/saveText';
import { downloadText, pickJsonFile } from '@/ui/save/saveFileIo';
import { el, setText, setVisible } from '@/ui/dom';
import { exportFileName, toExportText } from '@/save/exportEnvelope';
import { injectSaveTransferStyles } from '@/ui/save/saveTransferStyles';
import { readEnvelope } from '@/save/readEnvelope';
import { SAVE_MIGRATIONS } from '@/save/saveMigrationRegistry';
import { saveFailureText } from '@/save/saveFailureText';
import type { ProfileSession } from '@/save/profileSession';
import type { SaveFailure } from '@/save/saveResult';
import type { SaveProfile } from '@/save/saveContract';

/**
 * Past this, it is not a save. A profile with a full stash is kilobytes, and the cap is here so a
 * mis-picked video is refused by its size rather than read into a string first.
 */
const MAX_FILE_BYTES = 8_000_000;

/** What landed, for the screen that is about to be rebuilt around a different character. */
export interface ImportNews {
  readonly message: string;
  readonly advice: string;
}

export interface SaveTransferHandlers {
  readonly profile: ProfileSession;
  /**
   * A different character is now the live one, so whatever shows a name has to be rebuilt.
   *
   * The news goes with it. This panel is torn down by the rebuild it asks for, which would take the
   * line saying what was imported and where the backup went with it, and that line is the receipt
   * for the one action here that cannot be undone.
   */
  readonly onImported: (news: ImportNews) => void;
  /** Injected so a test can say what time it is, and so the filename is stable in a screenshot. */
  readonly now?: () => number;
}

export interface SaveTransfer {
  readonly root: HTMLElement;
  dispose(): void;
}

export function createSaveTransfer(handlers: SaveTransferHandlers): SaveTransfer {
  const removeStyles = injectSaveTransferStyles();
  const now = handlers.now ?? ((): number => Date.now());
  const profile = handlers.profile;

  let disposed = false;
  // Read through a call rather than directly. Every check below sits after an await, and a flag
  // only ever set inside `dispose` reads to the type checker as a constant that is never true.
  const gone = (): boolean => disposed;
  // What the confirm button will write, and null whenever there is nothing waiting on a press.
  let pending: SaveProfile | null = null;

  const root = el('section', 'save-transfer');
  const status = el('p', 'save-transfer-status');
  status.setAttribute('role', 'status');

  const exportButton = action(SAVE_EXPORT, 'save-transfer-export');
  const importButton = action(SAVE_IMPORT, 'save-transfer-import');
  const confirmButton = action(SAVE_REPLACE, 'save-transfer-confirm');
  const cancelButton = action(SAVE_CANCEL, 'save-transfer-cancel');

  const confirmRow = el('div', 'save-transfer-confirm-row');
  const confirmText = el('span', 'save-transfer-confirm-text');
  confirmRow.append(confirmText, confirmButton, cancelButton);
  setVisible(confirmRow, false);

  // Two buttons that say "Export save" and "Import save" need no title above them and no
  // paragraph beside them. The status line under them is where anything else gets said.
  const buttons = el('div', 'save-transfer-actions');
  buttons.append(exportButton, importButton);

  root.append(buttons, confirmRow, status);
  setVisible(status, false);

  const say = (message: string, advice = '', tone: 'good' | 'bad' | 'plain' = 'plain'): void => {
    setText(status, advice === '' ? message : `${message} ${advice}`);
    status.className = `save-transfer-status save-transfer-${tone}`;
    setVisible(status, true);
  };

  const busy = (on: boolean): void => {
    exportButton.disabled = on;
    importButton.disabled = on;
  };

  const clearPending = (): void => {
    pending = null;
    setVisible(confirmRow, false);
  };

  /** The character as it stands, as a file. Also the backup an import takes before it writes. */
  const download = (prefix: string): string => {
    const current = profile.current();
    const at = now();
    const fileName = `${prefix}${exportFileName(current, at)}`;
    downloadText(fileName, toExportText(current, at));
    return fileName;
  };

  exportButton.addEventListener('click', () => {
    clearPending();
    try {
      say(saveExported(download('')), '', 'good');
    } catch {
      say(SAVE_DOWNLOAD_REFUSED.message, SAVE_DOWNLOAD_REFUSED.advice, 'bad');
    }
  });

  importButton.addEventListener('click', () => {
    clearPending();
    busy(true);
    void offerImport().finally(() => {
      if (!gone()) busy(false);
    });
  });

  confirmButton.addEventListener('click', () => {
    const incoming = pending;
    if (incoming === null) return;
    clearPending();
    busy(true);
    void write(incoming).finally(() => {
      if (!gone()) busy(false);
    });
  });

  cancelButton.addEventListener('click', () => {
    clearPending();
    say(SAVE_CANCELLED);
  });

  /** Everything up to the confirm. Storage is not touched anywhere in here. */
  async function offerImport(): Promise<void> {
    const file = await pickJsonFile();
    if (gone()) return;
    if (file === null) {
      say(SAVE_NO_FILE);
      return;
    }

    if (file.size > MAX_FILE_BYTES) {
      refuse({ reason: 'fileTooLarge', bytes: file.size, limit: MAX_FILE_BYTES });
      return;
    }

    let text: string;
    try {
      text = await file.text();
    } catch {
      if (!gone()) say(SAVE_UNREADABLE_FILE.message, SAVE_UNREADABLE_FILE.advice, 'bad');
      return;
    }
    if (gone()) return;

    const read = readEnvelope(text, SAVE_MIGRATIONS);
    if (!read.ok) {
      refuse(read.failure);
      return;
    }

    const incoming = read.value;
    const target = await profile.describeIncoming(incoming);
    if (gone()) return;

    pending = incoming;
    setText(
      confirmText,
      target.replaces ? saveReplaceWarning(target.name) : saveAddWarning(incoming.name),
    );
    confirmButton.textContent = target.replaces ? SAVE_REPLACE : SAVE_ADD;
    setVisible(confirmRow, true);
    say(SAVE_CHECKED);
  }

  /** The backup, then the write. A backup that will not download stops the import. */
  async function write(incoming: SaveProfile): Promise<void> {
    let backup: string;
    try {
      backup = download('backup-');
    } catch {
      say(SAVE_BACKUP_REFUSED.message, SAVE_BACKUP_REFUSED.advice, 'bad');
      return;
    }

    const adopted = await profile.adopt(incoming);
    if (gone()) return;
    if (!adopted.ok) {
      refuse(adopted.failure);
      return;
    }

    handlers.onImported(saveImported(adopted.value.name, backup));
  }

  function refuse(failure: SaveFailure): void {
    if (gone()) return;
    const text = saveFailureText(failure);
    say(text.message, text.advice, 'bad');
  }

  return {
    root,
    dispose(): void {
      disposed = true;
      root.remove();
      removeStyles();
    },
  };
}

function action(label: string, className: string): HTMLButtonElement {
  const button = el('button', `save-transfer-button ${className}`, label);
  button.type = 'button';
  return button;
}
